// convex/lib/config/domains/reports.ts

import { validatorReport } from "../../../companies/reports";
import { validatorReportExecution } from "../../../companies/reports/report_executions";
import { validatorReportShare } from "../../../companies/reports/report_shares";
import {
  formatRule,
  dataSourceRule,
} from "../../../companies/reports/filter_rules";
import { validatorPipelineTracking } from "../../../system/pipeline_tracking";
import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// REPORTS DOMAIN - Status Values for i18n
// ============================================================================

export const REPORT_STATUSES = [
  "active",
  "paused",
  "completed",
  "archived",
] as const;
export const REPORT_EXECUTION_STATUSES = [
  "pending",
  "running",
  "completed",
  "failed",
] as const;

export const reportsDomain: DomainGroup = {
  id: "reports",
  // Camino A: INCLUIDO en on-prem (arquitectura unificada, sin divergencia).
  // La IP se protege por contrato de licencia, no por prune del build.
  editions: ["saas", "desktop", "onprem"],
  name: "Reportes y Análisis",
  description: "Generación y análisis de reportes",
  icon: "BarChart",
  order: 10,
  status: "active",
  // Acceso por defecto de company_user al grupo (el módulo `reports` lo baja a read)
  defaultAccess: { company_user: "full" },

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "reports",
    keys: {
      title: "reports.title",
      description: "reports.description",
    },
  },

  modules: [
    {
      id: "reports",
      name: "Reportes",
      description: "Crea, programa y ejecuta reportes personalizados",
      icon: "BarChart3",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      // company_user solo lee reportes (override del "full" del grupo)
      defaultAccess: { company_user: "read" },
      routes: ["/reports"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 7,
        status: "active",
      },

      i18n: {
        namespace: "reports.reports",
        keys: {
          title: "reports.reports.title",
          description: "reports.reports.description",
          longDescription: "reports.reports.longDescription",
          searchPlaceholder: "reports.reports.searchPlaceholder",
          emptyState: "reports.reports.emptyState",
          actions: {
            create: "reports.reports.actions.create",
            edit: "reports.reports.actions.edit",
            delete: "reports.reports.actions.delete",
            view: "reports.reports.actions.view",
            export: "reports.reports.actions.export",
          },
        },
        translatableFields: ["title", "description"],
        translatableEnums: [{ field: "status", values: REPORT_STATUSES }],
      },

      tables: [
        {
          tableName: "reports",
          schema: validatorReport,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["title", "description"],
          hasStatusIndex: true,
          customRules: [formatRule, dataSourceRule],
          searchIndex: { name: "search_reports", field: "title" },
        },
        {
          tableName: "report_executions",
          schema: validatorReportExecution,
          scope: "company" as TableScope,
          relations: [
            { key: "report", schema: validatorReport, tableName: "reports" },
            { key: "user", schema: validatorUsers, tableName: "users" },
          ],
          searchFields: ["status"],
          hasStatusIndex: false,
        },
        {
          tableName: "report_shares",
          schema: validatorReportShare,
          scope: "company" as TableScope,
          relations: [
            { key: "report", schema: validatorReport, tableName: "reports" },
            { key: "sharedBy", schema: validatorUsers, tableName: "users" },
            { key: "sharedWith", schema: validatorUsers, tableName: "users" },
          ],
          searchFields: [],
          hasStatusIndex: false,
        },
      ],
      readOnlyTables: [
        { tableName: "clients" },
        { tableName: "branches" },
        { tableName: "companies" },
      ],
    },
    // NOTE: Backend module sin UI route (routes vacío = no tiene ruta en app/routes/)
    {
      id: "pipeline_tracking",
      name: "Seguimiento de Procesos",
      description: "Monitoreo de pipelines y procesos",
      icon: "Workflow",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 999,
        status: "active",
      },

      i18n: {
        namespace: "reports.pipeline_tracking",
        keys: {
          title: "reports.pipeline_tracking.title",
          description: "reports.pipeline_tracking.description",
          longDescription: "reports.pipeline_tracking.longDescription",
          searchPlaceholder: "reports.pipeline_tracking.searchPlaceholder",
          emptyState: "reports.pipeline_tracking.emptyState",
          actions: {
            create: "reports.pipeline_tracking.actions.create",
            edit: "reports.pipeline_tracking.actions.edit",
            view: "reports.pipeline_tracking.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "pipeline_tracking",
          schema: validatorPipelineTracking,
          scope: "company" as TableScope,
          relations: [
            { key: "user", schema: validatorUsers, tableName: "users" },
          ],
          searchFields: ["entityType", "entityId", "stage"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [{ tableName: "branches" }],
    },
  ],
};
